import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { decode } from 'he';
import { useAccount } from '../../context/AccountContext';
import {
  GET_PUBLIC_SITE,
  GET_TINYCMS_ARTICLES,
  GET_TINYCMS_SITE,
  UPDATE_PUBLIC_SITE,
  UPDATE_TINYCMS_SITE,
  CREATE_TINYCMS_PREVIEW,
  PUBLISH_TINYCMS_CONTENT,
} from '../../graphql';
import { AnalyticsSettings } from '../settings/Analytics';
import { DomainsSettings } from '../settings/Domains';
import { ApprovalHistory, Finding, InkSection } from './shared';

/**
 * TinyCMS → Site. Visibility takes effect immediately. Theme edits stay on the
 * working copy until Publish. Domain and analytics keep their own saves.
 */

const COLOR_KEYS = ['background', 'text', 'accent', 'muted'] as const;
const COLOR_LABELS: Record<(typeof COLOR_KEYS)[number], string> = {
  background: 'Background',
  text: 'Text',
  accent: 'Links & accent',
  muted: 'Secondary text',
};
const HEX = /^#[0-9a-f]{6}$/i;
const FONTS: Record<string, string> = {
  serif: 'Georgia, serif',
  sans: 'var(--ink-font)',
  display: 'var(--ink-font)',
  mono: 'var(--ink-mono)',
};

const PRESETS: Record<
  string,
  {
    label: string;
    tag: string;
    help: string;
    colors: Record<string, string>;
    fonts: { heading: string; body: string };
    layout: { home: string; width: string; showAsk: boolean };
  }
> = {
  classic: {
    label: 'Classic',
    tag: 'Live today',
    help: 'The card-feed site you have today.',
    colors: { background: '#ffffff', text: '#0f172a', accent: '#2563eb', muted: '#64748b' },
    fonts: { heading: 'serif', body: 'sans' },
    layout: { home: 'feed', width: 'wide', showAsk: true },
  },
  editorial: {
    label: 'Editorial',
    tag: '',
    help: 'A reading-first list of articles, serif type, narrow column.',
    colors: { background: '#fbfaf7', text: '#1c1917', accent: '#9a3412', muted: '#78716c' },
    fonts: { heading: 'display', body: 'serif' },
    layout: { home: 'list', width: 'narrow', showAsk: false },
  },
  minimal: {
    label: 'Minimal',
    tag: '',
    help: 'Plain sans-serif, black on white.',
    colors: { background: '#ffffff', text: '#111111', accent: '#111111', muted: '#6b7280' },
    fonts: { heading: 'sans', body: 'sans' },
    layout: { home: 'list', width: 'standard', showAsk: false },
  },
};

const SURFACES: Array<{ key: string; name: string; desc: string; def: number }> = [
  { key: 'home', name: 'Homepage layout', desc: 'Modules, order, featured stories', def: 1 },
  { key: 'hubs', name: 'Topic hubs', desc: 'Landing pages for each topic', def: 0 },
  { key: 'nav', name: 'Navigation', desc: 'Header and footer links', def: 1 },
  { key: 'theme', name: 'Theme', desc: 'Type, color, logo', def: 2 },
  { key: 'public', name: 'Public fields', desc: 'Which custom fields readers can see', def: 2 },
];
const PERM_LABELS = [
  { label: 'Agents can edit', bg: 'var(--ink-green-bg)', color: 'var(--ink-green)' },
  { label: 'Ask first', bg: 'var(--ink-amber-bg)', color: 'var(--ink-amber)' },
  { label: 'Locked', bg: 'var(--ink-bg3)', color: 'var(--ink-ink2)' },
];

interface SiteForm {
  preset: string;
  colors: Record<string, string>;
  fonts: { heading: string; body: string };
  layout: { home: string; width: string; showAsk: boolean };
  logoUrl: string;
  logoAlt: string;
  coverUrl: string;
  coverAlt: string;
  iconUrl: string;
}

const toForm = (resolved: any): SiteForm => ({
  preset: resolved?.preset || 'classic',
  colors: { ...(resolved?.colors || {}) },
  // A Google Fonts choice is stored as { google, category, weights }; the form
  // holds its family name, so an untouched font is never re-sent.
  fonts: {
    heading: fontName(resolved?.fonts?.heading) || 'serif',
    body: fontName(resolved?.fonts?.body) || 'sans',
  },
  layout: {
    home: resolved?.layout?.home || 'feed',
    width: resolved?.layout?.width || 'wide',
    showAsk: resolved?.layout?.showAsk !== false,
  },
  logoUrl: resolved?.imagery?.logo?.url || '',
  logoAlt: resolved?.imagery?.logo?.alt || '',
  coverUrl: resolved?.imagery?.cover?.url || '',
  coverAlt: resolved?.imagery?.cover?.alt || '',
  iconUrl: resolved?.imagery?.icon?.url || '',
});

function fontName(v: unknown): string | null {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && typeof (v as { google?: unknown }).google === 'string') {
    return (v as { google: string }).google;
  }
  return null;
}

function diff(a: SiteForm, b: SiteForm) {
  const patch: Record<string, any> = {};
  if (a.preset !== b.preset) patch.preset = b.preset;
  const colors: Record<string, string> = {};
  for (const k of COLOR_KEYS)
    if ((a.colors[k] || '') !== (b.colors[k] || '')) colors[k] = b.colors[k];
  if (Object.keys(colors).length) patch.colors = colors;
  const fonts: Record<string, string> = {};
  if (a.fonts.heading !== b.fonts.heading) fonts.heading = b.fonts.heading;
  if (a.fonts.body !== b.fonts.body) fonts.body = b.fonts.body;
  if (Object.keys(fonts).length) patch.fonts = fonts;
  const layout: Record<string, any> = {};
  if (a.layout.home !== b.layout.home) layout.home = b.layout.home;
  if (a.layout.width !== b.layout.width) layout.width = b.layout.width;
  if (a.layout.showAsk !== b.layout.showAsk) layout.showAsk = b.layout.showAsk;
  if (Object.keys(layout).length) patch.layout = layout;
  const imagery: Record<string, any> = {};
  if (a.logoUrl !== b.logoUrl || a.logoAlt !== b.logoAlt) {
    imagery.logo = b.logoUrl ? { url: b.logoUrl, alt: b.logoAlt } : null;
  }
  if (a.coverUrl !== b.coverUrl || a.coverAlt !== b.coverAlt) {
    imagery.cover = b.coverUrl ? { url: b.coverUrl, alt: b.coverAlt } : null;
  }
  if (a.iconUrl !== b.iconUrl) imagery.icon = b.iconUrl ? { url: b.iconUrl } : null;
  if (Object.keys(imagery).length) patch.imagery = imagery;
  return patch;
}

const labelOf = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

type Vis = 'public' | 'password' | 'offline';

function visOf(
  account: { publicEnabled?: boolean; publicAccessProtected?: boolean } | null | undefined
): Vis {
  if (!account?.publicEnabled) return 'offline';
  if (account.publicAccessProtected) return 'password';
  return 'public';
}

export const CmsSite: React.FC = () => {
  const { accountId } = useAccount();
  const { data, loading, refetch } = useQuery(GET_TINYCMS_SITE, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: publicData, refetch: refetchPublic } = useQuery(GET_PUBLIC_SITE, {
    variables: { accountId },
    skip: !accountId,
  });
  const { data: articlesData } = useQuery(GET_TINYCMS_ARTICLES, {
    variables: { accountId, state: 'published' },
    skip: !accountId,
  });
  useEffect(() => {
    if (loading) return;
    const id = window.location.hash.replace('#', '');
    if (!id) return;
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [loading]);
  const [updateSite, { loading: saving }] = useMutation(UPDATE_TINYCMS_SITE);
  const [updatePublic] = useMutation(UPDATE_PUBLIC_SITE);
  const [publishContent, { loading: publishing }] = useMutation(PUBLISH_TINYCMS_CONTENT);
  const [createPreview, { loading: previewing }] = useMutation(CREATE_TINYCMS_PREVIEW);

  const site = data?.tinycmsSite;
  const account = publicData?.account;
  const loaded = useMemo(() => toForm(site?.resolved), [site]);
  const [form, setForm] = useState<SiteForm>(loaded);
  const [title, setTitle] = useState('');
  const [tagline, setTagline] = useState('');
  const [password, setPassword] = useState('');
  const [askPassword, setAskPassword] = useState(false);
  const [override, setOverride] = useState('');
  const [perms, setPerms] = useState<Record<string, number>>({});
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  useEffect(() => setForm(loaded), [loaded]);
  // The site's name and tagline live in the site document (they publish with
  // the theme, like everything readers see); the account's Public Site values
  // are the fallback until one is set.
  const shownTitle = site?.working?.identity?.title ?? account?.publicTitle ?? '';
  const shownTagline = site?.working?.identity?.tagline ?? account?.publicTagline ?? '';
  useEffect(() => {
    setTitle(shownTitle);
    setTagline(shownTagline);
  }, [shownTitle, shownTagline]);

  const patch = diff(loaded, form);
  const dirty = Object.keys(patch).length > 0;
  const unpublished = Boolean(site?.state?.hasUnpublishedChanges);
  const liveVersion = site?.state?.live?.version || 0;
  const findings: Finding[] = site?.state?.findings || [];
  const blocking = findings.filter((f) => f.severity === 'blocking').length;
  const vocab = site?.vocabulary || {
    presets: ['classic', 'editorial', 'minimal'],
    fonts: { heading: ['serif', 'sans', 'display', 'mono'], body: ['serif', 'sans'] },
    layouts: { home: ['feed', 'list', 'grid'], width: ['narrow', 'standard', 'wide'] },
  };

  const savedVis = visOf(account);
  const vis: Vis = askPassword ? 'password' : savedVis;
  // The server knows the real address (custom domain, platform host or free address).
  const host = site?.siteUrl ? String(site.siteUrl).replace(/^https?:\/\//, '') : null;
  const siteTitle = title.trim() || account?.name || 'Site';
  const titles: string[] = (articlesData?.tinycmsArticles || [])
    .map((a: { title?: string }) => decode(a.title || ''))
    .filter(Boolean)
    .slice(0, 3);

  const saveWorking = async () => {
    if (!dirty) return site?.workingHash as string;
    for (const key of COLOR_KEYS) {
      if (form.colors[key] && !HEX.test(form.colors[key])) {
        throw new Error(`${COLOR_LABELS[key]} needs a color like #1a2b3c.`);
      }
    }
    await updateSite({ variables: { accountId, input: patch } });
    const fresh = await refetch();
    return fresh.data?.tinycmsSite?.workingHash as string;
  };

  const publishTheme = async () => {
    setMessage(null);
    try {
      const hash = await saveWorking();
      const { data: published } = await publishContent({
        variables: {
          accountId,
          kind: 'site',
          action: 'publish',
          contentHash: hash,
          overrideReason: override.trim() || undefined,
        },
      });
      const r = published?.publishTinycmsContent;
      if (r?.ok) {
        setOverride('');
        setMessage({
          tone: 'ok',
          text: r.unchanged ? 'Already live.' : `Published theme v${r.version}.`,
        });
      } else {
        setMessage({ tone: 'error', text: r?.message || 'Publish was refused.' });
      }
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const preview = async () => {
    setMessage(null);
    const win = window.open('', '_blank');
    try {
      await saveWorking();
      const { data: created } = await createPreview({ variables: { accountId } });
      const url = created?.createTinycmsPreview?.url;
      if (win && url) win.location.href = url;
      else win?.close();
    } catch (err: any) {
      win?.close();
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const savePublic = async (input: Record<string, unknown>) => {
    setMessage(null);
    try {
      await updatePublic({ variables: { accountId, input } });
      await refetchPublic();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const chooseVis = async (next: Vis) => {
    if (next === 'password') {
      setAskPassword(true);
      return;
    }
    setAskPassword(false);
    if (next === 'offline') {
      await savePublic({ publicEnabled: false });
      return;
    }
    if (account?.publicAccessProtected) {
      if (
        !window.confirm(
          'Remove the password? Anyone with the address will be able to see this site.'
        )
      )
        return;
      await savePublic({ publicEnabled: true, publicAccessPassword: '' });
      return;
    }
    await savePublic({ publicEnabled: true });
  };

  const savePassword = async () => {
    if (!password.trim()) {
      setMessage({ tone: 'error', text: 'Enter a password to protect the site.' });
      return;
    }
    await savePublic({ publicEnabled: true, publicAccessPassword: password });
    setPassword('');
    setAskPassword(false);
  };

  const saveIdentity = async () => {
    const nextTitle = title.trim();
    const nextTagline = tagline.trim();
    if (nextTitle === shownTitle && nextTagline === shownTagline) return;
    setMessage(null);
    try {
      // Onto the working copy: readers see it when the theme is next published.
      await updateSite({
        variables: {
          accountId,
          input: { identity: { title: nextTitle || null, tagline: nextTagline || null } },
        },
      });
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  const resetPreset = async () => {
    setMessage(null);
    try {
      await updateSite({
        variables: {
          accountId,
          input: {
            colors: Object.fromEntries(COLOR_KEYS.map((k) => [k, null])),
            fonts: { heading: null, body: null },
            layout: { home: null, width: null, showAsk: null },
          },
        },
      });
      await refetch();
    } catch (err: any) {
      setMessage({ tone: 'error', text: err.message });
    }
  };

  if (loading && !site) {
    return (
      <div className="ink-body">
        <p style={{ color: 'var(--ink-ink3)' }}>Loading…</p>
      </div>
    );
  }
  if (!site) {
    return (
      <div className="ink-body">
        <p style={{ color: 'var(--ink-ink3)' }}>The site settings are unavailable.</p>
      </div>
    );
  }

  const visMeta: Record<Vis, { label: string; dot: string }> = {
    public: { label: 'Live · Public', dot: 'var(--ink-green)' },
    password: { label: 'Live · Password protected', dot: 'var(--ink-amber)' },
    offline: { label: 'Offline', dot: 'var(--ink-ink3)' },
  };
  const showBar = dirty || unpublished;
  const nextVersion = liveVersion + 1;
  const list = form.layout.home === 'list';
  const maxw =
    form.layout.width === 'narrow' ? '62%' : form.layout.width === 'standard' ? '80%' : '100%';
  const previewItems = titles.length
    ? titles
    : ['Your latest article', 'Another story', 'One more'];

  return (
    <div className="ink-body">
      {showBar ? (
        <div className="ink-site-bar">
          <span
            className="ink-dot"
            style={{ width: 8, height: 8, background: 'var(--ink-amber)' }}
          />
          <span style={{ font: '500 14px var(--ink-font)' }}>
            Theme changes are on the working copy.
          </span>
          <span className="ink-site-bar-note">
            {liveVersion
              ? `Readers still see theme v${liveVersion}.`
              : 'Readers still see the default theme.'}
          </span>
          <span className="ink-site-bar-actions">
            {dirty ? (
              <button
                type="button"
                className="ink-btn ink-btn-quiet"
                onClick={() => setForm(loaded)}
              >
                Discard
              </button>
            ) : null}
            <button
              type="button"
              className="ink-btn ink-btn-quiet"
              onClick={() => void preview()}
              disabled={previewing || saving}
            >
              Preview ↗
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => void publishTheme()}
              disabled={publishing || saving || (blocking > 0 && !override.trim())}
            >
              {publishing || saving ? 'Publishing…' : `Publish theme v${nextVersion}`}
            </button>
          </span>
          {blocking > 0 ? (
            <input
              className="ink-site-input"
              style={{ flex: '1 1 220px' }}
              value={override}
              onChange={(e) => setOverride(e.target.value)}
              placeholder="Override reason (recorded with this publish)"
            />
          ) : null}
        </div>
      ) : null}

      <div className="ink-site-hero">
        <div>
          <div className="ink-site-status">
            <span
              className="ink-dot"
              style={{ width: 8, height: 8, background: visMeta[vis].dot }}
            />
            {visMeta[vis].label}
          </div>
          <h1 className="ink-site-name">{siteTitle}</h1>
          {host ? (
            <a className="ink-site-url" href={`https://${host}`} target="_blank" rel="noreferrer">
              {host} ↗
            </a>
          ) : (
            <div className="ink-site-url" style={{ color: 'var(--ink-ink3)' }}>
              Choose a handle to give the site an address.
            </div>
          )}
        </div>
        <div
          className="ink-site-preview"
          style={{
            background: form.colors.background || '#fff',
            color: form.colors.text || '#111',
          }}
        >
          <span className="ink-site-badge">
            {dirty || unpublished ? 'Working theme' : 'Live theme'}
          </span>
          <div className="ink-site-preview-head">
            <span style={{ font: `700 17px/1.2 ${FONTS[form.fonts.heading] || FONTS.sans}` }}>
              {siteTitle}
            </span>
            {tagline ? (
              <span
                style={{
                  font: `400 12px/1.3 ${FONTS[form.fonts.body] || FONTS.sans}`,
                  color: form.colors.muted,
                }}
              >
                {tagline}
              </span>
            ) : null}
          </div>
          <div className="ink-site-preview-body" style={{ maxWidth: maxw }}>
            {list ? (
              previewItems.map((item) => (
                <div
                  key={item}
                  style={{
                    padding: '8px 0',
                    borderBottom: '1px solid color-mix(in srgb, currentColor 14%, transparent)',
                  }}
                >
                  <div style={{ font: `600 14px/1.3 ${FONTS[form.fonts.heading] || FONTS.sans}` }}>
                    {item}
                  </div>
                  <div
                    style={{
                      font: `400 11.5px/1.3 ${FONTS[form.fonts.body] || FONTS.sans}`,
                      color: form.colors.muted,
                      marginTop: 2,
                    }}
                  >
                    <span style={{ color: form.colors.accent }}>Read →</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="ink-site-cards">
                {previewItems.map((item) => (
                  <div key={item}>
                    <div className="ink-site-card-thumb" />
                    <div
                      style={{
                        font: `600 12px/1.3 ${FONTS[form.fonts.heading] || FONTS.sans}`,
                        marginTop: 6,
                      }}
                    >
                      {item}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {message ? (
        <p
          style={{
            margin: '16px 0 0',
            font: '400 14px var(--ink-font)',
            color: message.tone === 'error' ? 'var(--ink-red)' : 'var(--ink-green)',
          }}
        >
          {message.text}
        </p>
      ) : null}

      <InkSection
        id="visibility"
        title="Visibility"
        description="Changes take effect immediately. Nothing stays cached."
      >
        <div className="ink-site-choices">
          {(
            [
              ['public', 'Public', 'Anyone with the link, and search engines.'],
              ['password', 'Password', 'Private while you build it out.'],
              ['offline', 'Offline', 'Content stays here. The site is down.'],
            ] as const
          ).map(([key, label, desc]) => (
            <button
              key={key}
              type="button"
              className="ink-site-choice"
              data-on={vis === key}
              onClick={() => void chooseVis(key)}
            >
              <span className="ink-site-choice-title">
                {label}
                <span className="ink-site-radio" />
              </span>
              <span className="ink-site-choice-desc">{desc}</span>
            </button>
          ))}
        </div>
        {vis === 'password' ? (
          <div className="ink-site-pass">
            <input
              className="ink-site-input"
              type="password"
              value={password}
              autoComplete="new-password"
              placeholder={
                account?.publicAccessProtected
                  ? 'Leave blank to keep the current password'
                  : 'Set a password'
              }
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              onClick={() => void savePassword()}
            >
              Save password
            </button>
          </div>
        ) : null}
      </InkSection>

      <InkSection
        id="theme"
        title="Theme"
        description="The look of your public site. Edits stay on the working theme until you publish them."
      >
        <div className="ink-site-stack">
          <div className="ink-site-presets">
            {vocab.presets.map((key: string) => {
              const preset = PRESETS[key];
              if (!preset) return null;
              return (
                <button
                  key={key}
                  type="button"
                  className="ink-site-preset"
                  data-on={form.preset === key}
                  onClick={() =>
                    setForm({
                      ...form,
                      preset: key,
                      colors: { ...preset.colors },
                      fonts: { ...preset.fonts },
                      layout: { ...form.layout, ...preset.layout },
                    })
                  }
                >
                  <span className="ink-site-preset-art">
                    <span
                      style={{
                        font: `700 26px/1 ${FONTS[preset.fonts.heading] || FONTS.sans}`,
                        color: preset.colors.accent,
                      }}
                    >
                      Aa
                    </span>
                    <span className="ink-site-preset-bars">
                      <span style={{ width: '80%' }} />
                      <span style={{ width: '60%' }} />
                      <span style={{ width: '70%' }} />
                    </span>
                  </span>
                  <span className="ink-site-choice-title">
                    {preset.label}
                    <span
                      style={{
                        font: '400 12px var(--ink-font)',
                        color: 'var(--ink-ink3)',
                        fontWeight: 400,
                      }}
                    >
                      {preset.tag}
                    </span>
                  </span>
                  <span className="ink-site-preset-help">{preset.help}</span>
                </button>
              );
            })}
          </div>

          <div>
            <div className="ink-site-kicker">Colours</div>
            <div className="ink-site-swatches">
              {COLOR_KEYS.map((key) => (
                <label key={key} className="ink-site-swatch">
                  <input
                    type="color"
                    aria-label={COLOR_LABELS[key]}
                    value={HEX.test(form.colors[key] || '') ? form.colors[key] : '#000000'}
                    onChange={(e) =>
                      setForm({ ...form, colors: { ...form.colors, [key]: e.target.value } })
                    }
                  />
                  <span>
                    <span style={{ display: 'block', font: '500 13px var(--ink-font)' }}>
                      {COLOR_LABELS[key]}
                    </span>
                    <input
                      type="text"
                      value={form.colors[key] || ''}
                      onChange={(e) =>
                        setForm({ ...form, colors: { ...form.colors, [key]: e.target.value } })
                      }
                    />
                  </span>
                </label>
              ))}
            </div>
          </div>

          <div className="ink-site-controls">
            {(
              [
                ['heading', 'Headings', vocab.fonts.heading, form.fonts.heading],
                ['body', 'Body text', vocab.fonts.body, form.fonts.body],
                ['home', 'Home page', vocab.layouts.home, form.layout.home],
                ['width', 'Column width', vocab.layouts.width, form.layout.width],
              ] as const
            ).map(([key, label, options, value]) => (
              <div key={key} className="ink-site-control">
                <span style={{ font: '500 13px var(--ink-font)' }}>{label}</span>
                <span className="ink-seg">
                  {options.map((option: string) => (
                    <button
                      key={option}
                      type="button"
                      data-active={value === option}
                      onClick={() => {
                        if (key === 'heading' || key === 'body') {
                          setForm({ ...form, fonts: { ...form.fonts, [key]: option } });
                        } else {
                          setForm({ ...form, layout: { ...form.layout, [key]: option } });
                        }
                      }}
                    >
                      {labelOf(option)}
                    </button>
                  ))}
                </span>
              </div>
            ))}
          </div>

          <button
            type="button"
            className="ink-site-ask"
            onClick={() =>
              setForm({ ...form, layout: { ...form.layout, showAsk: !form.layout.showAsk } })
            }
          >
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', font: '500 14.5px var(--ink-font)' }}>
                Show “Ask the archive” on the site
              </span>
              <span className="ink-site-row-desc">
                Readers can ask questions answered from your atomized facts. Requires Atomize.
              </span>
            </span>
            <span className="ink-switch" data-on={form.layout.showAsk} />
          </button>

          <button
            type="button"
            className="ink-site-link"
            onClick={() => void resetPreset()}
            disabled={saving}
          >
            Use the preset’s own colours, fonts and layout
          </button>

          {findings.length ? (
            <div>
              {findings.map((f, i) => (
                <div
                  key={i}
                  className="ink-site-row-desc"
                  style={{
                    color: f.severity === 'blocking' ? 'var(--ink-red)' : 'var(--ink-amber)',
                  }}
                >
                  {f.message || f.code}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </InkSection>

      <InkSection
        id="surface"
        title="Surface"
        description="The parts of the site readers see, and what agents are allowed to change in each. Click a permission to change it."
      >
        <div className="ink-site-card">
          {SURFACES.map((row) => {
            const index = perms[row.key] ?? row.def;
            const perm = PERM_LABELS[index];
            return (
              <div className="ink-site-surface" key={row.key}>
                <div>
                  <div className="ink-site-surface-title">{row.name}</div>
                  <div className="ink-site-row-desc">{row.desc}</div>
                </div>
                <span />
                <button
                  type="button"
                  className="ink-site-perm"
                  style={{ background: perm.bg, color: perm.color }}
                  onClick={() =>
                    setPerms((prev) => ({
                      ...prev,
                      [row.key]: ((prev[row.key] ?? row.def) + 1) % 3,
                    }))
                  }
                >
                  {perm.label} ▾
                </button>
              </div>
            );
          })}
        </div>
      </InkSection>

      <InkSection
        id="identity"
        title="Identity"
        description="How the site introduces itself in browsers, search results and link previews. Publishes with the theme."
      >
        <div className="ink-site-fields">
          <label className="ink-site-label">
            Site title
            <input
              className="ink-site-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={() => void saveIdentity()}
            />
          </label>
          <label className="ink-site-label">
            Tagline
            <input
              className="ink-site-input"
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              onBlur={() => void saveIdentity()}
            />
          </label>
          <div className="ink-site-label">
            Logo
            <div className="ink-site-media">
              {form.logoUrl ? (
                <img className="ink-site-logo" src={form.logoUrl} alt="" />
              ) : (
                <span className="ink-site-logo" />
              )}
              <input
                className="ink-site-input"
                value={form.logoAlt}
                placeholder="Alt text, e.g. site logo"
                onChange={(e) => setForm({ ...form, logoAlt: e.target.value })}
              />
            </div>
            <input
              className="ink-site-input"
              value={form.logoUrl}
              placeholder="Logo URL"
              onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
            />
          </div>
          <div className="ink-site-label">
            Home cover image
            <div className="ink-site-media">
              {form.coverUrl ? (
                <img className="ink-site-cover" src={form.coverUrl} alt="" />
              ) : (
                <span className="ink-site-cover" />
              )}
              <input
                className="ink-site-input"
                value={form.coverAlt}
                placeholder="Alt text (required when there is an image)"
                onChange={(e) => setForm({ ...form, coverAlt: e.target.value })}
              />
            </div>
            <input
              className="ink-site-input"
              value={form.coverUrl}
              placeholder="Cover image URL"
              onChange={(e) => setForm({ ...form, coverUrl: e.target.value })}
            />
          </div>
          <div className="ink-site-label">
            Site icon
            <div className="ink-site-media">
              {form.iconUrl ? (
                <img className="ink-site-icon" src={form.iconUrl} alt="" />
              ) : (
                <span className="ink-site-icon" />
              )}
              <span className="ink-site-row-desc">
                Shown in browser tabs and on phone home screens. A square PNG, at least 180×180.
              </span>
            </div>
            <input
              className="ink-site-input"
              value={form.iconUrl}
              placeholder="Icon URL"
              onChange={(e) => setForm({ ...form, iconUrl: e.target.value })}
            />
          </div>
        </div>
      </InkSection>

      <InkSection
        id="domain"
        title="Domain"
        description="Connect your own domain with two DNS records. We set up the certificate."
      >
        <DomainsSettings embedded />
      </InkSection>

      <InkSection
        id="analytics"
        title="Analytics & code"
        description="Runs on the public site only. Previews never count as visits."
      >
        <AnalyticsSettings embedded />
      </InkSection>

      <InkSection title="History" description="Every published theme, with who approved it.">
        <ApprovalHistory kind="site" subjectId={accountId || undefined} variant="ink" />
      </InkSection>
    </div>
  );
};
